/**
 * Base URL del CDN de contenido (imágenes S3 vía Petflix).
 * Puede diferir de REACT_APP_PETFLIX_API_URL cuando staging API ≠ CDN de media.
 */
function getContentBaseUrl(isPetShopClient: boolean = false): string {
  if (isPetShopClient) {
    return 'https://store.noahsarkmarket.com/media';
  }

  if (process.env.REACT_APP_CONTENT_BASE_URL) {
    return process.env.REACT_APP_CONTENT_BASE_URL.replace(/\/+$/, '');
  }

  return 'https://dev.petflix.io/content';
}

/**
 * Genera la URL completa para una imagen basada en el path
 * @param key - El path de la imagen (ej: "6643776d2cd9c33d895d100c/1735911947264.jpg")
 * @param isPetShopClient - Si es true, usa el endpoint de PetShop
 * @returns La URL completa de la imagen
 */
export function getCompleteImagePath(key?: string | null, isPetShopClient: boolean = false): string | null {
  if (!key) {
    return null;
  }

  const contentKey = extractS3Key(key);

  if (contentKey) {
    const baseUrl = getContentBaseUrl(isPetShopClient);
    return `${baseUrl}?key=${encodeURIComponent(contentKey)}`;
  }

  // URL externa sin parámetro key (p. ej. CDN directo)
  if (key.startsWith('http://') || key.startsWith('https://')) {
    return key;
  }

  const baseUrl = getContentBaseUrl(isPetShopClient);
  return `${baseUrl}?key=${encodeURIComponent(key)}`;
}

/**
 * Genera la URL completa para una imagen desde un objeto media
 * @param mediaObject - El objeto media con el campo path
 * @param isPetShopClient - Si es true, usa el endpoint de PetShop
 * @returns La URL completa de la imagen o null si no hay path
 */
export function getImageUrlFromMedia(mediaObject?: { path?: string | null } | null, isPetShopClient: boolean = false): string | null {
  if (!mediaObject || !mediaObject.path) {
    return null;
  }

  return getCompleteImagePath(mediaObject.path, isPetShopClient);
}

/**
 * Extrae el key de S3 de una URL completa
 * @param url - URL completa o key de S3
 * @returns El key de S3 o null
 */
export function extractS3Key(url: string | null | undefined): string | null {
  if (!url) return null;
  
  // Si ya es un key (no empieza con http), devolverlo tal cual
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url;
  }
  
  // Extraer key de URLs tipo dev.petflix.io/content?key=...
  try {
    const urlObj = new URL(url);
    const key = urlObj.searchParams.get('key');
    return key ? decodeURIComponent(key) : null;
  } catch {
    // Si no es una URL válida, asumir que es un key
    return url;
  }
}

/**
 * Genera URLs optimizadas (small, medium, large) desde un key base
 * @param key - Key base de S3 (ej: "uploads/1234567890-image.jpg")
 * @param isPetShopClient - Si es true, usa el endpoint de PetShop
 * @returns Objeto con URLs para cada tamaño, o null si no se puede generar
 */
export function getOptimizedImageUrls(
  key: string | null | undefined,
  isPetShopClient: boolean = false
): { small: string; medium: string; large: string } | null {
  if (!key) return null;
  
  // Extraer key si viene en formato URL
  const s3Key = extractS3Key(key);
  if (!s3Key) return null;
  
  // Si el key ya está en formato optimizado (uploads/small/...), extraer el nombre base
  let baseKey = s3Key;
  const sizeMatch = s3Key.match(/^uploads\/(small|medium|large)\/(.+)$/);
  if (sizeMatch) {
    // Es una imagen nueva con versiones, extraer el nombre base
    baseKey = `uploads/${sizeMatch[2]}`;
  } else if (s3Key.startsWith('uploads/')) {
    // Es una imagen antigua, usar tal cual pero generar versiones
    // Extraer el nombre del archivo
    const fileName = s3Key.replace(/^uploads\//, '');
    baseKey = `uploads/${fileName}`;
  } else {
    // No es un formato reconocido, no generar versiones
    return null;
  }
  
  // Generar URLs para cada tamaño
  const fileName = baseKey.replace(/^uploads\//, '');
  const smallKey = `uploads/small/${fileName}`;
  const mediumKey = `uploads/medium/${fileName}`;
  const largeKey = `uploads/large/${fileName}`;
  
  return {
    small: getCompleteImagePath(smallKey, isPetShopClient) || '',
    medium: getCompleteImagePath(mediumKey, isPetShopClient) || '',
    large: getCompleteImagePath(largeKey, isPetShopClient) || '',
  };
}

/**
 * Genera srcset string para usar en atributo srcset de <img>
 * @param key - Key base de S3 o URL completa
 * @param isPetShopClient - Si es true, usa el endpoint de PetShop
 * @returns String srcset o null si no se puede generar
 */
export function generateSrcSet(
  key: string | null | undefined,
  isPetShopClient: boolean = false
): string | null {
  const urls = getOptimizedImageUrls(key, isPetShopClient);
  if (!urls) return null;
  
  return `${urls.small} 400w, ${urls.medium} 800w, ${urls.large} 1600w`;
}

/**
 * Obtiene la URL medium como fallback (para atributo src)
 * @param key - Key base de S3 o URL completa
 * @param isPetShopClient - Si es true, usa el endpoint de PetShop
 * @returns URL medium o la URL original si no hay versiones optimizadas
 */
export function getOptimizedImageFallback(
  key: string | null | undefined,
  isPetShopClient: boolean = false
): string | null {
  if (!key) return null;
  
  const urls = getOptimizedImageUrls(key, isPetShopClient);
  if (urls && urls.medium) {
    return urls.medium;
  }
  
  // Fallback a URL original
  return getCompleteImagePath(key, isPetShopClient);
}

